import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { AuditRecordListResult } from "@/modules/internal-audit/domain/types";
import { formatDateTime } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import {
  Badge,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EyeIcon } from "lucide-react";

type AuditRecordRow = AuditRecordListResult["data"][number];

type AuditRecordTableProps = {
  data: AuditRecordRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onView?: (id: string) => void;
};

const resultLabels: Record<string, string> = {
  pass: "ຜ່ານ",
  fail: "ບໍ່ຜ່ານ",
  needs_improvement: "ຕ້ອງປັບປຸງ",
};

const resultColors: Record<
  string,
  "default" | "secondary" | "outline" | "destructive"
> = {
  pass: "default",
  fail: "destructive",
  needs_improvement: "secondary",
};

const statusLabels: Record<string, string> = {
  draft: "ຮ່າງ",
  completed: "ແລ້ວ",
};

const statusColors: Record<
  string,
  "default" | "secondary" | "outline" | "destructive"
> = {
  draft: "secondary",
  completed: "default",
};

export function AuditRecordTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onView,
}: AuditRecordTableProps) {
  const { has } = usePermissions();

  const columns: TanstackReactTable.ColumnDef<AuditRecordRow>[] = [
    {
      id: "auditPlanName",
      header: "ຜະແນການກວດສອບ",
      cell: ({ row }) => (
        <span className="font-medium">{row.original.auditPlanName || "-"}</span>
      ),
      size: 200,
    },
    {
      id: "auditTypeName",
      header: "ປະເພດ",
      cell: ({ row }) => (
        <span className="text-sm">{row.original.auditTypeName || "-"}</span>
      ),
      size: 150,
    },
    {
      id: "departmentName",
      header: "ພະແນກ",
      cell: ({ row }) => (
        <span className="text-sm">{row.original.departmentName || "-"}</span>
      ),
      size: 120,
    },
    createSortableColumn<AuditRecordRow>("auditDate", "ວັນທີກວດສອບ", {
      size: 150,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDateTime(row.original.auditDate)}
        </span>
      ),
    }),
    {
      id: "auditorName",
      header: "ຜູ້ກວດສອບ",
      cell: ({ row }) => (
        <span className="text-sm">{row.original.auditorName || "-"}</span>
      ),
      size: 150,
    },
    {
      id: "overallResult",
      header: "ຜົນການກວດສອບ",
      cell: ({ row }) => (
        <Badge
          variant={resultColors[row.original.overallResult] || "secondary"}
        >
          {resultLabels[row.original.overallResult] ||
            row.original.overallResult}
        </Badge>
      ),
      size: 150,
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => (
        <Badge variant={statusColors[row.original.status] || "secondary"}>
          {statusLabels[row.original.status] || row.original.status}
        </Badge>
      ),
      size: 120,
    },
    createSortableColumn<AuditRecordRow>("createdAt", "ວັນທີສ້າງ", {
      size: 150,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDateTime(row.original.createdAt)}
        </span>
      ),
    }),
    {
      size: 100,
      id: "actions",
      cell: ({ row }: { row: { original: AuditRecordRow } }) => {
        const actions: {
          label: string;
          icon?: React.ReactNode;
          onClick: () => void;
        }[] = [];

        if (has("internal_audit:read") && onView) {
          actions.push({
            label: "ເບິ່ງລາຍລະອຽດ",
            icon: <EyeIcon className="h-4 w-4" />,
            onClick: () => onView(row.original.id),
          });
        }

        return <RowActions actions={actions} maxInline={2} />;
      },
    },
  ];

  return (
    <div data-tourid="record-table">
      <DataTable<AuditRecordRow, unknown>
        noDataMessage="ບໍ່ພົບບັນທຶກການກວດສອບ"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(sorting) => {
          if (!sorting[0] || !sorting[0].id || sorting[0].id === "") return;
          onSortingChange(sorting[0].id as string, !!sorting[0].desc);
        }}
      />
    </div>
  );
}
